import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Base } from "../framework/base";
import { transformVehicleTripSegments } from "../components/monitorView/monitorViewUtils";
import { WorkShiftDto } from "../models/workShift/workShift";
import {
    getCurrentVehicleShift,
    getVehicleShift,
    getVehicleShifts,
    getVehicleTripSegments,
} from "../services/vehicleService";
import { VehicleTripSegment } from "../models/vehicle/vehicleTripSegment";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { VehiclePositionInfo } from "../models/vehicle/vehiclePosition";
import { VehicleData } from "../models/monitorView/monitorViewTypes";
import {
    VehicleShiftDto,
    VehicleShiftListItemDto,
} from "../models/vehicleShift/vehicleShift";
import {
    positionStreetAddressReq,
    positionStreetAddressRes,
} from "../models/geocode/positionTypes";
import { getBatchOfGeocodes } from "../services/geocodingService";
import {
    getCurrentWorkShift,
    getWorkShiftsNew,
    getBasicWorkShifts,
} from "../services/workShiftService";

interface MonitorViewState {
    groupMapPoints: boolean;
    isWorkShiftView: boolean;
    isVehicleRouteView: boolean;
    isVehicleDetailView: boolean;
    selectedVehicleId: string | null;
    selectedEmployeeId: string | null;
    selectedWorkShiftTimeSlotId: string | null;
    selectedWorkShift: WorkShiftDto | null;
    selectedRouteId: string | null;
    hoveredRouteId: string | null;
    sidebarTitle: string;
    vehicleRoute: VehicleTripSegment[] | null;
    mapData: VehicleData[];
    tripStartDate: Date | null;
    tripEndDate: Date | null;
    vehicles: VehicleListItemDto[] | null;
    positions: VehiclePositionInfo[] | null;
    loading: boolean;
    error: string | null;
    segmentStoppedAddressesLoading: boolean;
    vehicleAddressLoading: boolean;
    vehicleStreetAddress: string;
    hoveredWorkShiftTimeSlotId: string;
    isVehicleShiftTabView: boolean;
    selectedVehicleShift: VehicleShiftDto | null;
    vehicleShiftLoading: boolean;
    vehicleShiftList: VehicleShiftListItemDto[];
    vehicleShiftListLoading: boolean;
    vehicleShiftSlotAddresses: Record<string, string>;
    vehicleShiftSlotAddressesLoading: boolean;
    currentEmployeeWorkShift: WorkShiftDto | null;
    currentEmployeeWorkShiftLoading: boolean;
    workShiftByDateLoading: boolean;
    employeeWorkShiftDates: string[];
    employeeWorkShiftDatesLoading: boolean;
}

export const initialState: MonitorViewState = {
    groupMapPoints: true,
    isWorkShiftView: false,
    isVehicleRouteView: false,
    isVehicleDetailView: false,
    selectedVehicleId: null,
    selectedEmployeeId: null,
    selectedWorkShiftTimeSlotId: null,
    selectedWorkShift: null,
    selectedRouteId: null,
    hoveredRouteId: null,
    sidebarTitle: "",
    vehicleRoute: null,
    mapData: null,
    tripStartDate: Base.dayjsToMinuteAccuracy().startOf("day").toDate(),
    tripEndDate: Base.dayjsToMinuteAccuracy().startOf("day").add(1, "day").toDate(),
    vehicles: null,
    positions: null,
    loading: false,
    error: null,
    segmentStoppedAddressesLoading: false,
    vehicleAddressLoading: false,
    vehicleStreetAddress: "",
    hoveredWorkShiftTimeSlotId: null,
    isVehicleShiftTabView: false,
    selectedVehicleShift: null,
    vehicleShiftLoading: false,
    vehicleShiftList: [],
    vehicleShiftListLoading: false,
    vehicleShiftSlotAddresses: {},
    vehicleShiftSlotAddressesLoading: false,
    currentEmployeeWorkShift: null,
    currentEmployeeWorkShiftLoading: false,
    workShiftByDateLoading: false,
    employeeWorkShiftDates: [],
    employeeWorkShiftDatesLoading: false,
};

export const fetchVehicleTripSegments = createAsyncThunk(
    "monitorView/fetchVehicleTripSegments",
    async (
        {
            vehicleId,
            startDate,
            endDate,
        }: { vehicleId: string; startDate: string; endDate: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await getVehicleTripSegments(
                vehicleId,
                startDate,
                endDate,
                false
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchGeocodedTripStopPositionAddresses = createAsyncThunk(
    "monitorView/fetchGeocodedStopPositionAddresses",
    async (request: positionStreetAddressReq[], { rejectWithValue }) => {
        try {
            const response = await getBatchOfGeocodes(request, true);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to geocode addresses");
        }
    }
);

export const fetchGeocodedVehicleAddress = createAsyncThunk<
    positionStreetAddressRes | null,
    positionStreetAddressReq,
    { rejectValue: unknown }
>(
    "monitorView/fetchGeocodedVehicleAddress",
    async (request: positionStreetAddressReq, { rejectWithValue }) => {
        try {
            const response = await getBatchOfGeocodes([request], true);
            if (!response?.data || response.data.length === 0) {
                return null;
            }
            return response.data[0];
        } catch (err) {
            return rejectWithValue(
                "Failed to geocode address for vehicle position"
            );
        }
    }
);

export const fetchGeocodedVehicleShiftSlotAddresses = createAsyncThunk(
    "monitorView/fetchGeocodedVehicleShiftSlotAddresses",
    async (request: positionStreetAddressReq[], { rejectWithValue }) => {
        try {
            const response = await getBatchOfGeocodes(request, true);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                "Failed to geocode vehicle shift slot addresses"
            );
        }
    }
);

export const fetchVehicleShiftList = createAsyncThunk(
    "monitorView/fetchVehicleShiftList",
    async (vehicleId: string, { rejectWithValue }) => {
        try {
            const response = await getVehicleShifts(vehicleId);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle shift list");
        }
    }
);

export const fetchVehicleShiftById = createAsyncThunk(
    "monitorView/fetchVehicleShiftById",
    async (vehicleShiftId: string, { rejectWithValue }) => {
        try {
            const response = await getVehicleShift(vehicleShiftId);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle shift");
        }
    }
);

export const fetchCurrentVehicleShift = createAsyncThunk(
    "monitorView/fetchCurrentVehicleShift",
    async (vehicleId: string, { rejectWithValue }) => {
        try {
            const response = await getCurrentVehicleShift(vehicleId);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle shift");
        }
    }
);

export const fetchCurrentEmployeeWorkShift = createAsyncThunk(
    "monitorView/fetchCurrentEmployeeWorkShift",
    async (employeeId: string, { rejectWithValue }) => {
        try {
            const response = await getCurrentWorkShift(employeeId, true);
            return response;
        } catch (err) {
            return rejectWithValue(
                "Failed to fetch current employee work shift"
            );
        }
    }
);

export const fetchEmployeeWorkShiftByDate = createAsyncThunk(
    "monitorView/fetchEmployeeWorkShiftByDate",
    async (
        { employeeId, date }: { employeeId: string; date: string },
        { rejectWithValue }
    ) => {
        try {
            const startDate = Base.dayjsToMinuteAccuracy(date).startOf("day").toISOString();
            const endDate = Base.dayjsToMinuteAccuracy(date).endOf("day").toISOString();
            const response = await getWorkShiftsNew(
                employeeId,
                startDate,
                endDate,
                true
            );
            return response.data?.[0] ?? null;
        } catch (err) {
            return rejectWithValue(
                "Failed to fetch employee work shift by date"
            );
        }
    }
);

export const fetchEmployeeWorkShiftDates = createAsyncThunk(
    "monitorView/fetchEmployeeWorkShiftDates",
    async (
        { employeeId, month }: { employeeId: string; month: string },
        { rejectWithValue }
    ) => {
        try {
            const startDate = Base.dayjsToMinuteAccuracy(month).startOf("month").toISOString();
            const endDate = Base.dayjsToMinuteAccuracy(month).endOf("month").toISOString();
            const response = await getBasicWorkShifts(
                employeeId,
                startDate,
                endDate,
                false
            );
            return (response.data ?? []).map((ws) => ws.effectiveDate);
        } catch (err) {
            return rejectWithValue("Failed to fetch employee work shift dates");
        }
    }
);

export const monitorViewSlice = createSlice({
    name: "monitorView",
    initialState,
    reducers: {
        setGroupMapPoints: (state, action: PayloadAction<boolean>) => {
            state.groupMapPoints = action.payload;
        },

        setIsWorkShiftView(state, action: PayloadAction<boolean>) {
            state.isWorkShiftView = action.payload;
        },
        toggleWorkShiftView(state) {
            state.isWorkShiftView = !state.isWorkShiftView;
        },
        setIsVehicleDetailView(state, action: PayloadAction<boolean>) {
            state.isVehicleDetailView = action.payload;
        },
        toggleVehicleDetailView(state) {
            state.isVehicleDetailView = !state.isVehicleDetailView;
        },
        setIsVehicleRouteView(state, action: PayloadAction<boolean>) {
            state.isVehicleRouteView = action.payload;
        },
        setSelectedVehicleId(state, action: PayloadAction<string | null>) {
            state.selectedVehicleId = action.payload;
        },
        setSelectedEmployeeId(state, action: PayloadAction<string | null>) {
            // Select or unselect if same selected twice
            if (state.selectedEmployeeId !== action.payload) {
                state.selectedEmployeeId = action.payload;
            } else {
                state.selectedEmployeeId = null;
            }
            // Clear also workshift data when employee is changed or unchecked
            state.selectedWorkShift = null;
            state.selectedWorkShiftTimeSlotId = null;
        },
        setSelectedWorkShiftTimeSlotId(
            state,
            action: PayloadAction<string | null>
        ) {
            // Select or unselect if same selected twice
            if (action.payload !== state.selectedWorkShiftTimeSlotId) {
                state.selectedWorkShiftTimeSlotId = action.payload;
            } else {
                state.selectedWorkShiftTimeSlotId = null;
            }
        },
        setSelectedWorkShift: (
            state,
            action: PayloadAction<WorkShiftDto | null>
        ) => {
            state.selectedWorkShift = action.payload;
        },
        setSelectedRouteId(state, action: PayloadAction<string | null>) {
            state.selectedRouteId = action.payload;
        },
        setHoveredRouteId(state, action: PayloadAction<string | null>) {
            state.hoveredRouteId = action.payload;
        },
        setSidebarTitle: (state, action: PayloadAction<string>) => {
            state.sidebarTitle = action.payload;
        },
        setVehicleRoute: (
            state,
            action: PayloadAction<VehicleTripSegment[]>
        ) => {
            state.vehicleRoute = action.payload;
        },
        setTripStartDate(state, action: PayloadAction<Date | null>) {
            state.tripStartDate = action.payload;
        },
        setTripEndDate(state, action: PayloadAction<Date | null>) {
            state.tripEndDate = action.payload;
        },
        resetTripDateRangeToToday(state) {
            state.tripStartDate = Base.dayjsToMinuteAccuracy().startOf("day").toDate();
            state.tripEndDate = Base.dayjsToMinuteAccuracy().startOf("day").add(1, "day").toDate();
        },
        setVehicles(state, action: PayloadAction<VehicleListItemDto[]>) {
            state.vehicles = action.payload;
        },
        setPositions(state, action: PayloadAction<VehiclePositionInfo[]>) {
            state.positions = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setHoveredWorkShiftTimeSlotId(
            state,
            action: PayloadAction<string | null>
        ) {
            state.hoveredWorkShiftTimeSlotId = action.payload;
        },
        setIsVehicleShiftTabView(state, action: PayloadAction<boolean>) {
            state.isVehicleShiftTabView = action.payload;
        },
        setSelectedVehicleShift(
            state,
            action: PayloadAction<VehicleShiftDto | null>
        ) {
            state.selectedVehicleShift = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVehicleTripSegments.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.vehicleRoute = null;
            })
            .addCase(fetchVehicleTripSegments.fulfilled, (state, action) => {
                state.loading = false;
                state.vehicleRoute = action.payload;
                state.mapData = transformVehicleTripSegments(action.payload);
            })
            .addCase(fetchVehicleTripSegments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(
                fetchGeocodedTripStopPositionAddresses.pending,
                (state) => {
                    state.segmentStoppedAddressesLoading = true;
                }
            )
            .addCase(
                fetchGeocodedTripStopPositionAddresses.fulfilled,
                (state, action) => {
                    const streetAddresses = action.payload;

                    // Convert to easy lookup: { id → data }
                    const byId = Object.fromEntries(
                        streetAddresses.map((g) => [g.id, g])
                    );

                    // Merge geocoded info into the route
                    state.vehicleRoute = (state.vehicleRoute ?? []).map(
                        (seg) => {
                            const geocoded = byId[seg.startTime];
                            if (geocoded) {
                                return {
                                    ...seg,
                                    streetAddress: geocoded.streetAddress,
                                };
                            }
                            return seg;
                        }
                    );
                    state.segmentStoppedAddressesLoading = false;
                    state.mapData = transformVehicleTripSegments(
                        state.vehicleRoute
                    );
                }
            )
            .addCase(
                fetchGeocodedTripStopPositionAddresses.rejected,
                (state) => {
                    state.segmentStoppedAddressesLoading = false;
                }
            )
            .addCase(fetchGeocodedVehicleAddress.pending, (state) => {
                state.vehicleAddressLoading = true;
            })
            .addCase(fetchGeocodedVehicleAddress.fulfilled, (state, action) => {
                const geocodeData = action.payload;
                state.vehicleAddressLoading = false;
                state.vehicleStreetAddress = geocodeData?.streetAddress;
                if (geocodeData?.error) {
                    console.error(geocodeData.error);
                }
            })
            .addCase(fetchGeocodedVehicleAddress.rejected, (state) => {
                state.vehicleAddressLoading = false;
            })
            .addCase(fetchCurrentVehicleShift.pending, (state) => {
                state.vehicleShiftLoading = true;
            })
            .addCase(fetchCurrentVehicleShift.fulfilled, (state, action) => {
                state.vehicleShiftLoading = false;
                state.selectedVehicleShift = action.payload;
            })
            .addCase(fetchCurrentVehicleShift.rejected, (state) => {
                state.vehicleShiftLoading = false;
                state.selectedVehicleShift = null;
            })
            .addCase(fetchVehicleShiftList.pending, (state) => {
                state.vehicleShiftListLoading = true;
            })
            .addCase(fetchVehicleShiftList.fulfilled, (state, action) => {
                state.vehicleShiftListLoading = false;
                state.vehicleShiftList = action.payload;
            })
            .addCase(fetchVehicleShiftList.rejected, (state) => {
                state.vehicleShiftListLoading = false;
                state.vehicleShiftList = [];
            })
            .addCase(fetchVehicleShiftById.pending, (state) => {
                state.vehicleShiftLoading = true;
            })
            .addCase(fetchVehicleShiftById.fulfilled, (state, action) => {
                state.vehicleShiftLoading = false;
                state.selectedVehicleShift = action.payload;
            })
            .addCase(fetchVehicleShiftById.rejected, (state) => {
                state.vehicleShiftLoading = false;
            })
            .addCase(
                fetchGeocodedVehicleShiftSlotAddresses.pending,
                (state) => {
                    state.vehicleShiftSlotAddressesLoading = true;
                }
            )
            .addCase(
                fetchGeocodedVehicleShiftSlotAddresses.fulfilled,
                (state, action) => {
                    state.vehicleShiftSlotAddressesLoading = false;
                    state.vehicleShiftSlotAddresses = Object.fromEntries(
                        action.payload.map((g) => [g.id, g.streetAddress])
                    );
                }
            )
            .addCase(
                fetchGeocodedVehicleShiftSlotAddresses.rejected,
                (state) => {
                    state.vehicleShiftSlotAddressesLoading = false;
                }
            )
            .addCase(fetchCurrentEmployeeWorkShift.pending, (state) => {
                state.currentEmployeeWorkShiftLoading = true;
            })
            .addCase(
                fetchCurrentEmployeeWorkShift.fulfilled,
                (state, action) => {
                    state.currentEmployeeWorkShiftLoading = false;
                    state.currentEmployeeWorkShift = action.payload;
                }
            )
            .addCase(fetchCurrentEmployeeWorkShift.rejected, (state) => {
                state.currentEmployeeWorkShiftLoading = false;
                state.currentEmployeeWorkShift = null;
            })
            .addCase(fetchEmployeeWorkShiftByDate.pending, (state) => {
                state.workShiftByDateLoading = true;
            })
            .addCase(
                fetchEmployeeWorkShiftByDate.fulfilled,
                (state, action) => {
                    state.workShiftByDateLoading = false;
                    state.selectedWorkShift = action.payload;
                }
            )
            .addCase(fetchEmployeeWorkShiftByDate.rejected, (state) => {
                state.workShiftByDateLoading = false;
                state.selectedWorkShift = null;
            })
            .addCase(fetchEmployeeWorkShiftDates.pending, (state) => {
                state.employeeWorkShiftDatesLoading = true;
            })
            .addCase(fetchEmployeeWorkShiftDates.fulfilled, (state, action) => {
                state.employeeWorkShiftDatesLoading = false;
                state.employeeWorkShiftDates = action.payload;
            })
            .addCase(fetchEmployeeWorkShiftDates.rejected, (state) => {
                state.employeeWorkShiftDatesLoading = false;
                state.employeeWorkShiftDates = [];
            });
    },
});

export const {
    setGroupMapPoints,
    setIsWorkShiftView,
    toggleWorkShiftView,
    setIsVehicleDetailView,
    toggleVehicleDetailView,
    setSelectedVehicleId,
    setSelectedEmployeeId,
    setSelectedWorkShiftTimeSlotId,
    setSelectedWorkShift,
    setSelectedRouteId,
    setHoveredRouteId,
    setSidebarTitle,
    setVehicleRoute,
    setTripStartDate,
    setTripEndDate,
    setVehicles,
    setPositions,
    setLoading,
    setIsVehicleRouteView,
    resetTripDateRangeToToday,
    setHoveredWorkShiftTimeSlotId,
    setIsVehicleShiftTabView,
    setSelectedVehicleShift,
} = monitorViewSlice.actions;
export const monitoriViewReducer = monitorViewSlice.reducer;
